import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Inventario.css";
import Modal from 'react-modal';

Modal.setAppElement('#root');

const Inventario = () => {
    const [productos, setProductos] = useState([]);
    const [formData, setFormData] = useState({
        id: "",
        nombre: "",
        precio: "",
        cantidad: "",
        descripcion: "",
        imagen: "",
    });
    const [editing, setEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [stockBajo, setStockBajo] = useState([]);
    const [showStockBajo, setShowStockBajo] = useState(false);
    const [historialModalIsOpen, setHistorialModalIsOpen] = useState(false);
    const [historialData, setHistorialData] = useState([]);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [paginaActual, setPaginaActual] = useState(1);
    const [productosPorPagina] = useState(15);

    useEffect(() => {
        fetchProductos();
    }, []);

    const fetchProductos = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get("https://backend-vbfh.onrender.com/api/inventario");
            setProductos(response.data);
            checkStockBajo(response.data);
        } catch (error) {
            console.error("Error al obtener productos:", error);
            setError("Error al cargar los productos. Intente nuevamente.");
        } finally {
            setLoading(false);
        }
    };

    const checkStockBajo = (productos) => {
        const productosStockBajo = productos.filter(producto => producto.cantidad < 10);
        setStockBajo(productosStockBajo);
    };

    const loadProductHistory = async (productoId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`https://backend-vbfh.onrender.com/api/inventario/historial/${productoId}`);
            
            if (!Array.isArray(response.data)) {
                throw new Error("Formato de datos incorrecto");
            }
    
            setHistorialData(response.data);
            setCurrentProduct(productos.find(p => p.id === productoId));
            setHistorialModalIsOpen(true);
        } catch (error) {
            console.error("Error al obtener historial:", error);
            setError(error.response?.data?.error || error.message || "Error al cargar historial");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            alert('La imagen es demasiado grande (máximo 5MB)');
            return;
        }

        const formData = new FormData();
        formData.append('imagen', file);

        try {
            const response = await axios.post('https://backend-vbfh.onrender.com/api/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setFormData(prev => ({
                ...prev,
                imagen: response.data.url
            }));
        } catch (error) {
            console.error('Error al subir la imagen:', error);
            alert('Error al subir la imagen: ' + (error.response?.data?.error || error.message));
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const formatDate = (dateString) => {
        const options = { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleString('es-ES', options);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Convertir el precio a número entero (eliminar cualquier caracter no numérico)
        const precioLimpio = formData.precio.replace(/\D/g, '');
        const precioEnCentavos = parseInt(precioLimpio) || 0;
        
        const productoData = {
            ...formData,
            precio: precioEnCentavos,
            cantidad: parseInt(formData.cantidad) || 0
        };

        try {
            if (editing) {
                // Para edición, enviamos el nuevo ID si es diferente
                if (formData.id !== editId.toString()) {
                    productoData.newId = formData.id;
                }
                await axios.put(`https://backend-vbfh.onrender.com/api/inventario/${editId}`, productoData);
            } else {
                await axios.post("https://backend-vbfh.onrender.com/api/inventario", productoData);
            }
            
            fetchProductos();
            setFormData({ id: "", nombre: "", precio: "", cantidad: "", descripcion: "", imagen: "" });
            setEditing(false);
            setEditId(null);
            setShowForm(false);
        } catch (error) {
            console.error("Error al guardar el producto:", error);
            setError(error.response?.data?.error || "Error al guardar el producto");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (producto) => {
        setFormData({
            id: producto.id,
            nombre: producto.nombre,
            precio: producto.precio.toString(),
            cantidad: producto.cantidad.toString(),
            descripcion: producto.descripcion,
            imagen: producto.imagen,
        });
        setEditing(true);
        setEditId(producto.id);
        setShowForm(true);
    };
    
    const handleDelete = async (id) => {
        if (!window.confirm("¿Está seguro de que desea eliminar este producto?")) {
            return;
        }

        setLoading(true);
        try {
            await axios.delete(`https://backend-vbfh.onrender.com/api/inventario/${id}`);
            fetchProductos();
        } catch (error) {
            console.error("Error al eliminar el producto:", error);
            setError("Error al eliminar el producto");
        } finally {
            setLoading(false);
        }
    };

    // Función para generar el HTML para imprimir
    const generarHTMLParaImprimir = (productosAImprimir) => {
        const fechaActual = new Date().toLocaleDateString('es-ES');
        const horaActual = new Date().toLocaleTimeString('es-ES');
        
        const html = `
            <html>
                <head>
                    <title>Inventario Completo</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            margin: 20px;
                            color: #333;
                        }
                        .header {
                            text-align: center;
                            margin-bottom: 30px;
                            border-bottom: 2px solid #3498db;
                            padding-bottom: 15px;
                        }
                        .header h1 {
                            color: #2c3e50;
                            margin-bottom: 5px;
                        }
                        .info-empresa {
                            text-align: center;
                            margin-bottom: 20px;
                            color: #666;
                        }
                        .resumen {
                            background-color: #f8f9fa;
                            padding: 15px;
                            border-radius: 8px;
                            margin-bottom: 20px;
                            border-left: 4px solid #3498db;
                        }
                        .resumen p {
                            margin: 5px 0;
                            font-weight: bold;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 20px;
                            font-size: 12px;
                        }
                        th {
                            background-color: #3498db;
                            color: white;
                            padding: 10px;
                            text-align: left;
                            font-weight: bold;
                            border: 1px solid #2980b9;
                        }
                        td {
                            padding: 8px 10px;
                            border: 1px solid #ddd;
                        }
                        tr:nth-child(even) {
                            background-color: #f9f9f9;
                        }
                        tr:hover {
                            background-color: #f1f1f1;
                        }
                        .stock-bajo {
                            background-color: #ffebee !important;
                        }
                        .footer {
                            margin-top: 30px;
                            text-align: center;
                            color: #666;
                            font-size: 12px;
                            border-top: 1px solid #ddd;
                            padding-top: 15px;
                        }
                        @media print {
                            body {
                                margin: 0;
                                padding: 15px;
                            }
                            .no-print {
                                display: none;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>📦 INVENTARIO COMPLETO</h1>
                        <div class="info-empresa">
                            <p><strong>Distribuciones Aristizabal</strong></p>
                            <p>Fecha de impresión: ${fechaActual} - ${horaActual}</p>
                        </div>
                    </div>
                    
                    <div class="resumen">
                        <p>Total de productos: ${productosAImprimir.length}</p>
                        <p>Total en stock: ${productosAImprimir.reduce((total, producto) => total + parseInt(producto.cantidad), 0)} unidades</p>
                        <p>Valor total: ${formatPrice(productosAImprimir.reduce((total, producto) => total + (parseInt(producto.precio) * parseInt(producto.cantidad)), 0))}</p>
                    </div>

                    <table>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nombre</th>
                                <th>Precio</th>
                                <th>Stock</th>
                                <th>Descripción</th>
                                <th>Valor Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${productosAImprimir.map((producto) => `
                                <tr class="${producto.cantidad < 10 ? 'stock-bajo' : ''}">
                                    <td>${producto.id}</td>
                                    <td>${producto.nombre}</td>
                                    <td>${formatPrice(producto.precio)}</td>
                                    <td>${producto.cantidad}</td>
                                    <td>${producto.descripcion || "-"}</td>
                                    <td>${formatPrice(producto.precio * producto.cantidad)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="footer">
                        <p>Documento generado automáticamente - Distribuciones Aristizabal</p>
                        <p>Página 1 de 1</p>
                    </div>
                </body>
            </html>
        `;
        return html;
    };

    // Función para imprimir el inventario
    const imprimirInventario = () => {
        const productosAImprimir = searchQuery ? filteredProducts : productos;
        
        if (productosAImprimir.length === 0) {
            alert("No hay productos para imprimir.");
            return;
        }

        const ventanaImpresion = window.open("", "_blank");
        const html = generarHTMLParaImprimir(productosAImprimir);
        ventanaImpresion.document.write(html);
        ventanaImpresion.document.close();
        
        // Esperar a que se cargue el contenido antes de imprimir
        ventanaImpresion.onload = function() {
            ventanaImpresion.print();
            // Cerrar la ventana después de imprimir (opcional)
            // ventanaImpresion.onafterprint = function() {
            //     ventanaImpresion.close();
            // };
        };
    };

    const filteredProducts = productos.filter((producto) => {
        const searchLower = searchQuery.toLowerCase();
        return (
            producto.nombre.toLowerCase().includes(searchLower) ||
            producto.id.toString().includes(searchQuery) ||
            producto.descripcion?.toLowerCase().includes(searchLower)
        );
    });

    // Lógica de paginación
    const indexUltimoProducto = paginaActual * productosPorPagina;
    const indexPrimerProducto = indexUltimoProducto - productosPorPagina;
    const productosActuales = filteredProducts.slice(indexPrimerProducto, indexUltimoProducto);
    const totalPaginas = Math.ceil(filteredProducts.length / productosPorPagina);

    const cambiarPagina = (numeroPagina) => {
        setPaginaActual(numeroPagina);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const paginaSiguiente = () => {
        if (paginaActual < totalPaginas) {
            setPaginaActual(paginaActual + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const paginaAnterior = () => {
        if (paginaActual > 1) {
            setPaginaActual(paginaActual - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Generar números de página para mostrar
    const numerosPagina = [];
    const paginasAMostrar = 5;
    
    let inicioPaginas = Math.max(1, paginaActual - Math.floor(paginasAMostrar / 2));
    let finPaginas = Math.min(totalPaginas, inicioPaginas + paginasAMostrar - 1);
    
    if (finPaginas - inicioPaginas + 1 < paginasAMostrar) {
        inicioPaginas = Math.max(1, finPaginas - paginasAMostrar + 1);
    }
    
    for (let i = inicioPaginas; i <= finPaginas; i++) {
        numerosPagina.push(i);
    }

    const totalProductos = productos.reduce((total, producto) => total + parseInt(producto.cantidad), 0);
    const totalValorMercancia = productos.reduce((total, producto) => total + (parseInt(producto.precio) * parseInt(producto.cantidad)), 0);

    return (
        <div className="inventario-container">
            <h2>📦 Panel de Inventario 📦</h2>

            {error && <div className="error-message">{error}</div>}
            {loading && <div className="loading-indicator">Cargando...</div>}

            <div className="resumen-inventario">
                <p>Total de productos en stock: <strong>{totalProductos}</strong></p>
                <p>Valor total de la mercancía: <strong>{formatPrice(totalValorMercancia)}</strong></p>
            </div>

            {stockBajo.length > 0 && (
                <div className="stock-bajo-alert">
                    <h3 onClick={() => setShowStockBajo(!showStockBajo)} style={{cursor: 'pointer'}}>
                        {showStockBajo ? '▼' : '▶'} ⚠️ Productos con stock bajo (menos de 10 unidades)
                    </h3>
                    {showStockBajo && (
                        <ul>
                            {stockBajo.map((producto) => (
                                <li key={producto.id}>
                                    <strong>{producto.nombre}</strong> - {producto.cantidad} unidades
                                    <button onClick={() => handleEdit(producto)} className="small-button">Editar</button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <div className="search-and-add">
                <input
                    type="text"
                    placeholder="Buscar por nombre, ID o descripción..."
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPaginaActual(1); // Resetear a primera página al buscar
                    }}
                    className="search-bar"
                />
                <div className="action-buttons-header">
                    <button 
                        onClick={() => {
                            setShowForm(!showForm);
                            if (showForm) {
                                setEditing(false);
                                setFormData({ id: "", nombre: "", precio: "", cantidad: "", descripcion: "", imagen: "" });
                            }
                        }} 
                        className="add-product-button"
                    >
                        {showForm ? "Cancelar" : "➕ Añadir Producto"}
                    </button>
                    <button 
                        onClick={imprimirInventario}
                        className="print-inventario-button"
                        title="Imprimir inventario completo"
                    >
                        🖨️ Imprimir
                    </button>
                </div>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="form-producto">
                    <div className="form-group">
                        <label>ID del Producto:</label>
                        <input
                            type="number"
                            name="id"
                            placeholder="ID"
                            value={formData.id}
                            onChange={handleInputChange}
                            required
                            disabled={editing}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Nombre:</label>
                        <input
                            type="text"
                            name="nombre"
                            placeholder="Nombre del producto"
                            value={formData.nombre}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Precio (COP):</label>
                        <input
                            type="text"
                            name="precio"
                            placeholder="Precio"
                            value={formData.precio}
                            onChange={(e) => {
                                // Permitir solo números
                                const rawValue = e.target.value.replace(/\D/g, '');
                                setFormData({
                                    ...formData,
                                    precio: rawValue,
                                });
                            }}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Cantidad en Stock:</label>
                        <input
                            type="number"
                            name="cantidad"
                            placeholder="Cantidad"
                            value={formData.cantidad}
                            onChange={handleInputChange}
                            min="0"
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Descripción:</label>
                        <textarea
                            name="descripcion"
                            placeholder="Descripción del producto"
                            value={formData.descripcion}
                            onChange={handleInputChange}
                            rows="3"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Imagen del Producto:</label>
                        <input
                            type="file"
                            name="imagen"
                            onChange={handleImageUpload}
                            accept="image/*"
                        />
                        {formData.imagen && (
                            <div className="image-preview">
                                <img src={formData.imagen} alt="Vista previa" width="100" />
                                <button 
                                    type="button" 
                                    onClick={() => setFormData({...formData, imagen: ""})}
                                    className="remove-image-button"
                                >
                                    ✖ Eliminar imagen
                                </button>
                            </div>
                        )}
                    </div>
                    
                    <button type="submit" className="submit-button" disabled={loading}>
                        {editing ? "💾 Guardar Cambios" : "➕ Añadir Producto"}
                    </button>
                </form>
            )}

            {/* Información de paginación */}
            <div className="info-paginacion-inventario">
                <p>
                    Mostrando {productosActuales.length} de {filteredProducts.length} productos 
                    (Página {paginaActual} de {totalPaginas})
                </p>
            </div>

            <div className="table-container">
                <table className="productos-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Nombre</th>
                            <th>Precio</th>
                            <th>Stock</th>
                            <th>Descripción</th>
                            <th>Imagen</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {productosActuales.length > 0 ? (
                            productosActuales.map((producto) => (
                                <tr key={producto.id} className={producto.cantidad < 10 ? "stock-bajo" : ""}>
                                    <td>{producto.id}</td>
                                    <td>{producto.nombre}</td>
                                    <td>{formatPrice(producto.precio)}</td>
                                    <td>{producto.cantidad}</td>
                                    <td>{producto.descripcion || "-"}</td>
                                    <td>
                                        {producto.imagen ? (
                                            <img 
                                                src={producto.imagen} 
                                                alt={producto.nombre} 
                                                width="50" 
                                                onClick={() => window.open(producto.imagen, '_blank')}
                                                className="product-image"
                                            />
                                        ) : "-"}
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button 
                                                onClick={() => handleEdit(producto)} 
                                                className="edit-button"
                                            >
                                                ✏️ Editar
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(producto.id)} 
                                                className="delete-button"
                                            >
                                                🗑️ Eliminar
                                            </button>
                                            <button 
                                                onClick={() => loadProductHistory(producto.id)} 
                                                className="history-button"
                                            >
                                                📜 Historial
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="no-results">
                                    {searchQuery ? "No se encontraron productos que coincidan con la búsqueda" : "No hay productos registrados"}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            {totalPaginas > 1 && (
                <div className="paginacion-container">
                    <button 
                        className={`boton-paginacion ${paginaActual === 1 ? 'boton-deshabilitado' : ''}`}
                        onClick={paginaAnterior}
                        disabled={paginaActual === 1}
                    >
                        ‹ Anterior
                    </button>
                    
                    {/* Mostrar primera página si no está en el rango */}
                    {inicioPaginas > 1 && (
                        <>
                            <button 
                                className={`numero-pagina ${1 === paginaActual ? 'pagina-actual' : ''}`}
                                onClick={() => cambiarPagina(1)}
                            >
                                1
                            </button>
                            {inicioPaginas > 2 && <span className="puntos-suspensivos">...</span>}
                        </>
                    )}

                    {/* Números de página */}
                    {numerosPagina.map(numero => (
                        <button
                            key={numero}
                            className={`numero-pagina ${numero === paginaActual ? 'pagina-actual' : ''}`}
                            onClick={() => cambiarPagina(numero)}
                        >
                            {numero}
                        </button>
                    ))}

                    {/* Mostrar última página si no está en el rango */}
                    {finPaginas < totalPaginas && (
                        <>
                            {finPaginas < totalPaginas - 1 && <span className="puntos-suspensivos">...</span>}
                            <button 
                                className={`numero-pagina ${totalPaginas === paginaActual ? 'pagina-actual' : ''}`}
                                onClick={() => cambiarPagina(totalPaginas)}
                            >
                                {totalPaginas}
                            </button>
                        </>
                    )}

                    <button 
                        className={`boton-paginacion ${paginaActual === totalPaginas ? 'boton-deshabilitado' : ''}`}
                        onClick={paginaSiguiente}
                        disabled={paginaActual === totalPaginas}
                    >
                        Siguiente ›
                    </button>
                </div>
            )}

            <Modal
                isOpen={historialModalIsOpen}
                onRequestClose={() => setHistorialModalIsOpen(false)}
                contentLabel="Historial de Inventario"
                className="modal"
                overlayClassName="overlay"
                style={{
                    content: {
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        maxHeight: '80vh',
                        width: '80%',
                        maxWidth: '900px'
                    }
                }}
            >
                <div className="modal-header">
                    <h2>📜 Historial de Inventario: {currentProduct?.nombre}</h2>
                    <button 
                        onClick={() => setHistorialModalIsOpen(false)} 
                        className="close-modal-button"
                    >
                        &times;
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-indicator">Cargando historial...</div>
                    ) : historialData.length > 0 ? (
                        <table className="historial-table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Cant. Anterior</th>
                                    <th>Cant. Nueva</th>
                                    <th>Diferencia</th>
                                    <th>Motivo</th>
                                    <th>Usuario</th>
                                    <th>Pedido</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historialData.map((item) => (
                                    <tr key={item.id}>
                                        <td>{formatDate(item.fecha)}</td>
                                        <td>{item.cantidad_anterior}</td>
                                        <td>{item.cantidad_nueva}</td>
                                        <td className={item.diferencia > 0 ? "positivo" : "negativo"}>
                                            {item.diferencia > 0 ? `+${item.diferencia}` : item.diferencia}
                                        </td>
                                        <td>{item.motivo}</td>
                                        <td>{item.usuario_nombre || item.usuario || 'Sistema'}</td>
                                        <td>
                                            {item.numeroremision ? (
                                                <a 
                                                    href={`/pedidos?search=${item.numeroremision}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                >
                                                    {item.numeroremision}
                                                </a>
                                            ) : 'N/A'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="no-history">No hay registros de historial para este producto.</div>
                    )}
                </div>
                <div className="modal-footer">
                    <button 
                        onClick={() => setHistorialModalIsOpen(false)} 
                        className="close-button"
                    >
                        Cerrar
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default Inventario;